import { ReactNode, useEffect } from 'react';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { IconButton } from '@/components/IconButton';
import { Portal } from '@/components/Portal';

type Props = {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleInfo?: string;
};

export function Modal({ children, isOpen, onClose, title, titleInfo }: Props) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = isOpen ? 'hidden' : overflow;
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  const onOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const titleInfoComponent = titleInfo ? <span className="text-gray-500 font-normal">{titleInfo}</span> : null;
  const titleComponent = title ? (
    <h3 className="text-lg font-semibold text-gray-900">
      {title} {titleInfoComponent}
    </h3>
  ) : null;

  return isOpen ? (
    <Portal>
      <div
        className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-8 cursor pointer"
        onMouseDown={onOverlayMouseDown}
        id="ksai-modal-overlay"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Modal"
          className="bg-white rounded-lg w-full max-w-[90vw] max-h-[90vh] p-6 flex flex-col relative"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            {titleComponent}

            <IconButton onClick={onClose} aria-label="Close modal" className="absolute p-2 top-4 right-4">
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </IconButton>
          </div>
          <div className="flex-1 h-full min-h-0 overflow-hidden">{children}</div>
        </div>
      </div>
    </Portal>
  ) : null;
}
